import { useCallback, useContext, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography } from '@altermaind/web/ui';
import { LayoutContext } from '@/_shared/components/PageLayout';
import { Tabs } from '@/_shared/components/Tabs';
import { TutorialDialog } from '@/_shared/components/TutorialDialog';
import { TutorialKeys } from '@/_shared/constants';
import { useDialog, useTabs, useTutorialCheck } from '@/_shared/hooks';
import { TableAssessmentHardSkills } from '../components/TableAssessmentHardSkills/TableAssessmentHardSkills';
import { TableAssessmentSoftSkills } from '../components/TableAssessmentSoftSkills/TableAssessmentSoftSkills';
import { getReportAssessmentExportCall } from '../state/api';

export const ReportsAssessmentsScreen = () => {
  // eslint-disable-next-line @altermaind/use-translations-scope
  const t = useTranslations('reports');
  const { execute: executeGetReportAssessmentExport, loading: loadingGetReportAssessmentExport } =
    getReportAssessmentExportCall.hook();
  const { openDialog: openDialogReportsAssessmentsTutorial } = useDialog(
    'reportsAssessmentsTutorial',
  );
  const { setLayout } = useContext(LayoutContext);
  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const tabsInitialState = [
    {
      title: t('tabs.softSkills'),
      index: 0,
      active: true,
      dataTestId: 'softSkills',
    },
    {
      title: t('tabs.hardSkills'),
      index: 1,
      active: false,
      dataTestId: 'hardSkills',
    },
  ];

  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  const slides = [
    {
      title: t('assessments.tutorial.slides.slide1.title'),
      description: t('assessments.tutorial.slides.slide1.description'),
      assetName: 'tutorialReportsAssessmentsSlide1',
    },
    {
      title: t('assessments.tutorial.slides.slide3.title'),
      description: t('assessments.tutorial.slides.slide3.description'),
      assetName: 'tutorialReportsAssessmentsSlide3',
    },
  ];

  const handleExportClick = useCallback(() => {
    executeGetReportAssessmentExport({});
  }, [executeGetReportAssessmentExport]);

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.ReportsAssessments)) {
      openDialogReportsAssessmentsTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogReportsAssessmentsTutorial]);

  useEffect(() => {
    setLayout({ title: 'reports' });
  }, [setLayout, t]);

  return (
    <>
      <Typography as='span' variant='h1' mr='xxxs'>
        {t('assessments.pageTitle')}
      </Typography>
      <Box display='flex' justifyContent='space-between' alignItems='center' mt='s'>
        <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
        <Button onClick={handleExportClick} disabled={loadingGetReportAssessmentExport}>
          <Typography as='span' variant='p2/bold' mr='xxxs'>
            {t('export')}
          </Typography>
          <Svg name='download' fill='primary/text' size='s' />
        </Button>
      </Box>
      {tabs.map(tab => {
        return (
          <Box key={tab.title} display='flex' flexDirection='column'>
            {tab.index === 0 && tab.active && <TableAssessmentSoftSkills />}
            {tab.index === 1 && tab.active && <TableAssessmentHardSkills />}
          </Box>
        );
      })}
      <TutorialDialog
        translationsKey='reports.assessments.tutorial'
        dialogKey='reportsAssessmentsTutorial'
        slides={slides}
      />
    </>
  );
};
